import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, ScrollText } from "lucide-react";
import { Helmet } from "react-helmet-async";
import { api } from "../../lib/api";
import { apiErrorMessage } from "../../lib/errors";
import {
  Cell,
  EmptyState,
  HeadCell,
  MatchupCell,
  fmtPct,
  dqTone,
} from "../../components/terminal/DataTable";

const SPORT_TABS = [
  { key: "", label: "ALL" },
  { key: "mlb", label: "MLB" },
  { key: "nba", label: "NBA" },
  { key: "nhl", label: "NHL" },
  { key: "nfl", label: "NFL" },
  { key: "mma", label: "MMA" },
];

function ResultBadge({ result }) {
  if (result === "win") {
    return <span className="mono text-[10px] px-2 py-0.5 border border-[#00D4FF] text-[#00D4FF]">WIN</span>;
  }
  if (result === "loss") {
    return <span className="mono text-[10px] px-2 py-0.5 border border-red-500 text-red-400">LOSS</span>;
  }
  if (result === "push") {
    return <span className="mono text-[10px] px-2 py-0.5 border border-zinc-600 text-zinc-400">PUSH</span>;
  }
  return <span className="mono text-[10px] text-zinc-600">—</span>;
}

function OverallStats({ stats }) {
  if (!stats) return null;
  const clv = stats.avg_clv_pct;
  return (
    <div className="grid grid-cols-2 md:grid-cols-5 gap-0 border border-zinc-800 mb-6" data-testid="ledger-stats">
      <div className="p-4 border-r border-zinc-800">
        <div className="overline">TOTAL</div>
        <div className="font-display text-3xl mt-1">{stats.total}</div>
      </div>
      <div className="p-4 border-r border-zinc-800">
        <div className="overline">WINS</div>
        <div className="font-display text-3xl mt-1 text-[#00D4FF]">{stats.wins}</div>
      </div>
      <div className="p-4 border-r border-zinc-800">
        <div className="overline">LOSSES</div>
        <div className="font-display text-3xl mt-1 text-red-400">{stats.losses}</div>
      </div>
      <div className="p-4 border-r border-zinc-800">
        <div className="overline">HIT RATE</div>
        <div className="font-display text-3xl mt-1">{(stats.hit_rate_pct ?? 0).toFixed(1)}%</div>
      </div>
      <div className="p-4">
        <div className="overline">AVG CLV</div>
        <div className={`font-display text-3xl mt-1 ${clv === null ? "text-zinc-500" : clv >= 0 ? "text-[#00D4FF]" : "text-red-400"}`}>
          {clv === null || clv === undefined ? "—" : clv >= 0 ? `+${clv}%` : `${clv}%`}
        </div>
      </div>
    </div>
  );
}

export default function Ledger() {
  const [sport, setSport] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async (s) => {
    setLoading(true);
    try {
      const params = {};
      if (s) params.sport = s;
      const { data: d } = await api.get("/terminal/ledger", { params });
      setData(d);
    } catch (err) {
      console.warn("ledger load failed", err);
      toast.error(apiErrorMessage(err, "Could not load ledger"));
      setData({ overall: { total: 0, wins: 0, losses: 0, pushes: 0, hit_rate_pct: 0, avg_clv_pct: null }, picks: [] });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(sport); }, [sport, load]);

  const picks = data?.picks || [];

  return (
    <div data-testid="ledger-page">
      <Helmet>
        <title>Pick Accountability Ledger · Every AI pick, graded · Martingale Playbook</title>
        <meta
          name="description"
          content="Public, grading-transparent ledger of every AI sports betting pick we've ever made. Each entry shows the lean, model fair line, DK price, result, and Closing Line Value."
        />
      </Helmet>

      <div className="mb-4">
        <h2 className="font-display text-2xl flex items-center gap-2">
          <ScrollText size={20} className="text-[#00D4FF]" />
          Pick accountability ledger
        </h2>
        <p className="mono text-[11px] text-zinc-500 mt-1">
          Every AI pick we've ever made, auto-graded against ESPN final scores.
          CLV = price we locked vs closing DraftKings price. Positive CLV = we beat the close.
        </p>
      </div>

      <OverallStats stats={data?.overall} />

      <div className="flex border-b border-zinc-800 mb-0" data-testid="ledger-sport-tabs">
        {SPORT_TABS.map((t) => (
          <button
            key={t.key || "all"}
            onClick={() => setSport(t.key)}
            data-testid={`ledger-tab-${t.key || "all"}`}
            className={`px-4 py-2.5 mono text-[11px] tracking-wider border-r border-zinc-800 ${
              sport === t.key
                ? "bg-zinc-900/60 text-[#00D4FF] border-b-2 border-b-[#00D4FF]"
                : "text-zinc-400 hover:text-zinc-200"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {loading && (
        <div className="py-8 text-center mono text-xs text-zinc-500">
          <Loader2 size={16} className="inline animate-spin mr-2" />
          Grading…
        </div>
      )}
      {!loading && picks.length === 0 && (
        <EmptyState
          testid="ledger-empty"
          message="No graded picks yet. Come back after tonight's slate wraps — every AI pick will show up here with its CLV and result."
        />
      )}

      {!loading && picks.length > 0 && (
        <div className="border border-zinc-800 overflow-x-auto" data-testid="ledger-table">
          <table className="w-full min-w-[900px]">
            <thead>
              <tr>
                <HeadCell>DATE</HeadCell>
                <HeadCell>MATCHUP</HeadCell>
                <HeadCell>LEAN</HeadCell>
                <HeadCell>RESULT</HeadCell>
                <HeadCell>SCORE</HeadCell>
                <HeadCell className="text-right">EDGE</HeadCell>
                <HeadCell className="text-right">CLV</HeadCell>
                <HeadCell className="text-right">DQ</HeadCell>
              </tr>
            </thead>
            <tbody>
              {picks.map((p) => (
                <tr key={p.key} className="hover:bg-zinc-900/40" data-testid={`ledger-row-${p.key}`}>
                  <Cell className="text-zinc-400">
                    {p.start_time ? new Date(p.start_time).toLocaleDateString() : "—"}
                  </Cell>
                  <Cell><MatchupCell row={p} /></Cell>
                  <Cell className="text-zinc-200">{p.lean}</Cell>
                  <Cell><ResultBadge result={p.result} /></Cell>
                  <Cell className="text-zinc-500">{p.final_score || "—"}</Cell>
                  <Cell className="text-right">{fmtPct(p.edge_pct)}</Cell>
                  <Cell className={`text-right ${p.clv_pct === null || p.clv_pct === undefined ? "text-zinc-500" : p.clv_pct >= 0 ? "text-[#00D4FF]" : "text-red-400"}`}>
                    {p.clv_pct === null || p.clv_pct === undefined ? "—" : p.clv_pct >= 0 ? `+${p.clv_pct}%` : `${p.clv_pct}%`}
                  </Cell>
                  <Cell className={`text-right ${dqTone(p.data_quality_score)}`}>
                    {p.data_quality_score ?? 0}
                  </Cell>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
